/**
 * Hujjatni o'z sahifangizda imzolatish.
 *
 * Imzolash oynasining o'zi Shaffof ID dan keladi: E-IMZO bilan ishlash
 * (CAPIWS, apparat tokenlar, kalit tanlash) va yuzni tasdiqlash o'sha
 * yerda qoladi — ularni har bir tizim qaytadan yozishi shart emas.
 * Foydalanuvchi esa sizning sahifangizdan chiqmaydi.
 *
 * Oyna ALOHIDA darcha (popup) bo'lib ochiladi, ramka (iframe) emas.
 * Sababi brauzerda: seans cookie'si `SameSite=Lax` bilan yoziladi va
 * uni brauzer BOSHQA saytning ramkasiga yubormaydi — ya'ni ramka
 * ichida foydalanuvchi har doim «kirmagan» bo'lib ko'rinadi va hujjat
 * ochilmaydi (401). Darcha esa to'liq huquqli oyna: seans ham, kirish
 * sahifasi ham, E-IMZO ham odatdagidek ishlaydi.
 */

export type SignOptions = {
  /**
   * Natija ma'lum bo'lishi bilan chaqiriladi — oyna hali ochiq.
   *
   * Foydalanuvchi natijani uch soniya ko'rib turadi, shu vaqtda siz
   * o'z sahifangizni yangilab qo'yishingiz mumkin.
   */
  onResult?: (result: SignResult) => void
  /** Oyna yopilgandan keyin chaqiriladi */
  onFinish?: (result: SignResult) => void
  /** Interfeys tili */
  locale?: 'uz' | 'ru' | 'en'
  /**
   * Oyna qanday ochilsin.
   *
   *   popup — alohida darcha (standart, hamma joyda ishlaydi)
   *   frame — sahifangiz ichida ramka
   *
   * `frame` faqat sayt Shaffof ID bilan BIR domenda bo'lsa ishlaydi
   * (masalan `app.shaffofqurilish.uz` va `id.shaffofqurilish.uz`).
   * Boshqa domendan ramka ichiga seans cookie'si tushmaydi.
   */
  mode?: 'popup' | 'frame'
}

export type SignResult = {
  /**
   * signed   — imzolandi
   * rejected — foydalanuvchi imzolashdan bosh tortdi
   * closed   — oynani shunchaki yopdi (hech narsa qilmadi)
   * unknown  — oyna Shaffof ID bilan bog'lana olmadi, natija noma'lum
   *
   * `unknown` kelsa, hujjat holatini SERVERINGIZDAN so'rang
   * (`GET /api/documents/{uuid}`): oyna yopildi, lekin u imzo
   * qo'yilgan-qo'yilmaganini bizga aytmadi. Sababi konsolda yoziladi.
   */
  status: 'signed' | 'rejected' | 'closed' | 'unknown'
  uuid: string
}

type FrameMessage = {
  source?: string
  type?: string
  uuid?: string
}

/** Darcha o'lchami: telefonda ham, kompyuterda ham o'qishga qulay */
const POPUP = { width: 540, height: 820 }

/**
 * Oyna uslublari yuklanganiga ishonch hosil qiladi.
 *
 * Uslublar elementning `style` atributida emas, alohida faylda:
 * qat'iy CSP (`style-src 'self'`) o'rnatilgan saytlarda brauzer
 * atributdagi uslubni bloklaydi va oyna umuman chizilmay qolardi.
 * Sahifa `shaffof-id.css` ni o'zi ulagan bo'lsa, ikkinchi marta
 * qo'shilmaydi.
 */
function ensureStyles(origin: string): void {
  const href = `${origin}/sdk/shaffof-id.css`

  if (document.querySelector(`link[href="${href}"], link[href$="/shaffof-id.css"]`)) return

  const link = document.createElement('link')

  link.rel = 'stylesheet'
  link.href = href

  document.head.append(link)
}

/**
 * Oyna umuman gapirmadi — demak u bizni «o'ziniki» deb tanimadi.
 *
 * Natija xabari faqat TEKSHIRILGAN manzilga yuboriladi: Shaffof ID
 * sahifangiz manzilini hujjatni yuborgan proyektning `redirect_uris`
 * ro'yxati bilan solishtiradi. Mos kelmasa — sahifa oddiy ko'rinishda
 * ishlaydi, imzo ham qo'yiladi, lekin natija sizga qaytmaydi va oyna
 * yopilgach «hech narsa qilmadi» degan xulosa chiqib qolardi.
 */
function warnSilent(origin: string): void {
  console.warn(
    `[shaffof-id] Imzolash oynasidan birorta xabar kelmadi. Natija noma'lum.\n`
    + `Tekshiring: sahifangiz manzili (${window.location.origin}) `
    + `${origin} dagi proyektning redirect_uris ro'yxatida bormi?\n`
    + 'Hujjat holatini serveringizdan so\'rang: GET /api/documents/{uuid}',
  )
}

/**
 * Imzolash oynasini ochadi va natijani kutadi.
 *
 * Xabarlar faqat Shaffof ID manzilidan qabul qilinadi: boshqa oyna
 * «imzolandi» deb yozib yuborolmaydi.
 */
export function openSigningFrame(
  issuer: string,
  uuid: string,
  options: SignOptions = {},
): Promise<SignResult> {
  const origin = new URL(issuer).origin

  const params = new URLSearchParams({ embed: '1', origin: window.location.origin })
  if (options.locale) params.set('lang', options.locale)

  const url = `${origin}/documents/${encodeURIComponent(uuid)}?${params}`

  ensureStyles(origin)

  return options.mode === 'frame'
    ? inFrame(origin, url, uuid, options)
    : inPopup(origin, url, uuid, options)
}

/* -------------------------------------------------------------- darcha */

function inPopup(
  origin: string,
  url: string,
  uuid: string,
  options: SignOptions,
): Promise<SignResult> {
  return new Promise<SignResult>((resolve) => {
    let done = false
    let outcome: SignResult['status'] = 'closed'
    let popup: Window | null = null
    let watch = 0

    // Oynadan hech bo'lmaganda bitta xabar keldimi
    let heard = false

    const overlay = element('div', 'shaffof-id-sign')
    overlay.setAttribute('data-shaffof-sign', '')

    const box = element('div', 'shaffof-id-sign__card')

    const title = element('p', 'shaffof-id-sign__title')
    title.textContent = 'Imzolash oynasi ochildi'

    const hint = element('p', 'shaffof-id-sign__hint')
    hint.textContent = 'Hujjatni o\'sha oynada imzolang. Bu sahifa joyida qoladi.'

    const again = button('Oynani qayta ochish', 'shaffof-id-sign__btn--primary')
    const cancel = button('Bekor qilish')

    function open() {
      const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP.width) / 2))
      const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP.height) / 3))

      popup = window.open(
        url,
        'shaffof-id-sign',
        `width=${POPUP.width},height=${POPUP.height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
      )

      if (popup === null) {
        // Darcha bloklangan: qayta ochishni foydalanuvchining o'zi
        // bossin — bosish bo'lsa brauzer ruxsat beradi
        title.textContent = 'Brauzer oynani bloklab qo\'ydi'
        hint.textContent = 'Shu sayt uchun qalqib chiquvchi oynalarga ruxsat bering yoki qayta urinib ko\'ring.'

        return
      }

      title.textContent = 'Imzolash oynasi ochildi'
      hint.textContent = 'Hujjatni o\'sha oynada imzolang. Bu sahifa joyida qoladi.'
      popup.focus()

      // Oynani qo'l bilan yopsa ham natijasiz qolmaymiz
      window.clearInterval(watch)
      watch = window.setInterval(() => {
        if (popup?.closed) finish(outcome)
      }, 500)
    }

    function finish(status: SignResult['status']) {
      if (done) return
      done = true

      window.clearInterval(watch)
      window.removeEventListener('message', onMessage)
      document.removeEventListener('keydown', onKey)
      overlay.remove()

      if (popup !== null && ! popup.closed) popup.close()

      // Oyna jim yopildi: imzolangan bo'lishi ham mumkin — «yopdi» deb
      // aytib qo'ysak, tizim imzoni e'tiborsiz qoldirardi
      if (! heard && status === 'closed') {
        warnSilent(origin)
        status = 'unknown'
      }

      const result: SignResult = { status, uuid }

      options.onFinish?.(result)
      resolve(result)
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== origin) return

      const data = event.data as FrameMessage

      if (data?.source !== 'shaffof-id') return

      heard = true

      if (data.type === 'signed' || data.type === 'rejected') {
        outcome = data.type
        options.onResult?.({ status: outcome, uuid })

        return
      }

      if (data.type === 'closed') finish(outcome)
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') finish(outcome)
    }

    again.onclick = () => (popup === null || popup.closed ? open() : popup.focus())
    cancel.onclick = () => finish(outcome)

    window.addEventListener('message', onMessage)
    document.addEventListener('keydown', onKey)

    box.append(title, hint, again, cancel)
    overlay.append(box)
    document.body.append(overlay)

    open()
  })
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)

  node.className = className

  return node
}

function button(label: string, modifier = ''): HTMLButtonElement {
  const node = element('button', `shaffof-id-sign__btn ${modifier}`.trim())

  node.type = 'button'
  node.textContent = label

  return node
}

/* --------------------------------------------------------------- ramka */

/**
 * Sahifa ichidagi ramka.
 *
 * Faqat bir domendagi saytlar uchun: boshqa domenda seans cookie'si
 * ramkaga tushmaydi va hujjat ochilmaydi.
 */
function inFrame(
  origin: string,
  url: string,
  uuid: string,
  options: SignOptions,
): Promise<SignResult> {
  return new Promise<SignResult>((resolve) => {
    const overlay = element('div', 'shaffof-id-sign')
    overlay.setAttribute('data-shaffof-sign', '')

    const box = element('div', 'shaffof-id-sign__box')

    const frame = element('iframe', 'shaffof-id-sign__frame')
    frame.src = url
    frame.title = 'Shaffof ID — hujjatni imzolash'
    // Imzolash E-IMZO bilan ishlaydi va kamera kerak bo'lishi mumkin
    frame.allow = 'camera'

    const close = element('button', 'shaffof-id-sign__close')
    close.type = 'button'
    close.setAttribute('aria-label', 'Yopish')
    close.textContent = '✕'

    let done = false

    // Imzolangani ma'lum bo'ldi, lekin oyna hali ochiq: foydalanuvchi
    // natijani ko'rib tursin. Oynani Shaffof ID sahifasining o'zi
    // yopishni so'raydi («closed») — uch soniyadan keyin yoki tugma bilan
    let outcome: SignResult['status'] = 'closed'

    // Ramkadan hech bo'lmaganda bitta xabar keldimi
    let heard = false

    function finish(status: SignResult['status']) {
      if (done) return
      done = true

      window.removeEventListener('message', onMessage)
      document.removeEventListener('keydown', onKey)
      overlay.remove()

      if (! heard && status === 'closed') {
        warnSilent(origin)
        status = 'unknown'
      }

      const result: SignResult = { status, uuid }

      options.onFinish?.(result)
      resolve(result)
    }

    function onMessage(event: MessageEvent) {
      // Boshqa manzildan kelgan xabarga ishonmaymiz
      if (event.origin !== origin) return

      const data = event.data as FrameMessage

      if (data?.source !== 'shaffof-id') return

      heard = true

      if (data.type === 'signed' || data.type === 'rejected') {
        outcome = data.type

        options.onResult?.({ status: outcome, uuid })

        return
      }

      if (data.type === 'closed') finish(outcome)
    }

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') finish(outcome)
    }

    // Qo'lda yopilganda ham natija yo'qolmaydi: imzolangan bo'lsa
    // «signed» qaytadi, aks holda «closed»
    close.onclick = () => finish(outcome)
    overlay.onclick = (event) => { if (event.target === overlay) finish(outcome) }

    window.addEventListener('message', onMessage)
    document.addEventListener('keydown', onKey)

    box.append(close, frame)
    overlay.append(box)
    document.body.append(overlay)
  })
}
